import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
    Trophy, Ticket, Loader2, Crown, Clock, CheckCircle2, Lock, Sparkles, PartyPopper,
} from "lucide-react";
import { BannerDisplay } from "@/components/Banner";
import { HelpRequestButton } from "@/components/HelpRequestButton";

const PACKAGES = [
    { key: "single", entries: 1, price: 2.0, label: "1 Entry", subtitle: "Just dip a toe in" },
    { key: "five", entries: 5, price: 9.0, label: "5 Entries", subtitle: "Save $1 — better odds", badge: "Popular" },
    { key: "ten", entries: 10, price: 15.0, label: "10 Entries", subtitle: "Best value — save $5", badge: "Best Value" },
];

function formatRemaining(endsAt) {
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime() - Date.now();
    if (ms <= 0) return { expired: true, label: "ENDED" };
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hrs = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    let label;
    if (days > 0) label = `${days}d ${hrs}h ${mins}m`;
    else if (hrs > 0) label = `${hrs}h ${mins}m ${secs}s`;
    else if (mins > 0) label = `${mins}m ${secs}s`;
    else label = `${secs}s`;
    return { expired: false, label, urgent: totalSec < 3600 };
}

function Countdown({ endsAt }) {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!endsAt) return undefined;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [endsAt]);
    const r = formatRemaining(endsAt);
    if (!r) {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-mono text-[#8b8ba0]">
                <Clock className="w-3.5 h-3.5" /> No deadline
            </span>
        );
    }
    if (r.expired) {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-mono text-[#ef4444]">
                <Lock className="w-3.5 h-3.5" /> ENTRIES LOCKED
            </span>
        );
    }
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-mono ${r.urgent ? "text-[#ef4444] animate-pulse" : "text-[#facc15]"}`}>
            <Clock className="w-3.5 h-3.5" /> {r.label}
        </span>
    );
}

function PrizePreview({ g }) {
    if (g.prize_type === "mythic_banner") {
        if (g.prize_banner_image) {
            const banner = {
                image: g.prize_banner_image,
                animation: g.prize_banner_animation || "pulse_purple",
                color: g.prize_banner_color || "#a855f7",
                name: g.prize_banner_name,
            };
            return (
                <div className="relative">
                    <BannerDisplay banner={banner} height={130} />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-widest bg-[#1f0f2e] border border-[#a855f7]55 text-[#a855f7]">
                        MYTHIC PRIZE
                    </div>
                </div>
            );
        }
        return (
            <div className="h-32 rounded-md flex items-center justify-center bg-[#1f0f2e] border border-[#a855f7]55">
                <span className="text-xs font-mono text-[#a855f7]">Banner prize</span>
            </div>
        );
    }
    // Premium weeks prize
    const weeks = g.prize_premium_weeks || 3;
    return (
        <div className="h-32 rounded-md flex flex-col items-center justify-center bg-gradient-to-br from-[#0f1f3a] to-[#1a0c2e] border border-[#facc15]55 relative overflow-hidden">
            <Crown className="w-9 h-9 text-[#facc15] drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]" />
            <p className="font-display text-2xl tracking-widest text-white mt-1">{weeks} WEEK{weeks > 1 ? "S" : ""}</p>
            <p className="text-xs font-mono uppercase text-[#facc15] tracking-widest">SPORTS PREMIUM</p>
        </div>
    );
}

export default function Giveaways() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [selectedPkg, setSelectedPkg] = useState({}); // map giveaway_id -> package key
    const [buying, setBuying] = useState(null); // giveaway being purchased
    const fetchTimerRef = useRef(null);

    const fetchAll = useCallback(async () => {
        try {
            const res = await api.get("/giveaways");
            setItems(res.data.items || []);
        } catch (e) { /* noop */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchAll();
        fetchTimerRef.current = setInterval(fetchAll, 15000); // refresh entry counts every 15s
        return () => clearInterval(fetchTimerRef.current);
    }, [fetchAll, user]);

    const handleBuy = async (g, pkgKey) => {
        if (!user) {
            toast("Log in to enter giveaways");
            nav("/login?next=/giveaways");
            return;
        }
        setBusy(`${g.id}::${pkgKey}`);
        try {
            const res = await api.post(`/giveaways/${g.id}/buy-entries`, {
                package: pkgKey,
                origin_url: window.location.origin,
            });
            if (res.data.url) {
                window.location.href = res.data.url;
            } else {
                toast.error("Could not create checkout");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to start checkout");
        } finally {
            setBusy(null);
        }
    };

    const active = items.filter((g) => g.status === "active" && !g.locked);
    const lockedOnes = items.filter((g) => g.status === "locked" || (g.status === "active" && g.locked));
    const drawn = items.filter((g) => g.status === "drawn");

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-10 sm:py-14">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="mb-8">
                        <p className="text-xs font-mono uppercase text-[#facc15] tracking-widest">Giveaways</p>
                        <h1 className="font-display text-4xl sm:text-5xl tracking-widest text-white inline-flex items-center gap-2">
                            <Trophy className="w-7 h-7 text-[#facc15]" /> WIN MYTHICS &amp; FREE PREMIUM
                        </h1>
                        <p className="mt-1 text-sm text-[#b7b7c6]">$2 per entry. More entries = better odds. Winner pulled live by the owner.</p>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-72 rounded-xl pp-bg-1" />)}
                        </div>
                    ) : items.length === 0 ? (
                        <Card className="pp-bg-1 border-[#242436] text-white">
                            <CardContent className="p-10 text-center">
                                <Trophy className="w-10 h-10 mx-auto text-[#facc15] mb-2" />
                                <p className="font-display text-xl tracking-widest">NO GIVEAWAYS YET</p>
                                <p className="mt-1 text-sm text-[#b7b7c6]">Check back soon — mythic banner drops and free Premium weeks coming up!</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-10">
                            {/* Active section */}
                            {active.length > 0 && (
                                <div data-testid="giveaways-active-section">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="inline-block w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
                                        <p className="font-display text-xl tracking-widest text-[#22c55e]">LIVE GIVEAWAYS ({active.length})</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <AnimatePresence>
                                            {active.map((g) => {
                                                const sel = selectedPkg[g.id] || "single";
                                                const pkg = PACKAGES.find((p) => p.key === sel) || PACKAGES[0];
                                                const buyKey = `${g.id}::${sel}`;
                                                return (
                                                    <motion.div
                                                        key={g.id}
                                                        layout
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.95 }}
                                                        transition={{ duration: 0.25 }}
                                                    >
                                                        <Card className="pp-bg-1 border-[#242436] text-white overflow-hidden" data-testid={`giveaway-card-${g.id}`}>
                                                            <CardContent className="p-5 space-y-4">
                                                                <PrizePreview g={g} />
                                                                <div>
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <p className="font-display text-xl tracking-widest text-white truncate">{g.title}</p>
                                                                        <Countdown endsAt={g.ends_at} />
                                                                    </div>
                                                                    {g.description && <p className="mt-1 text-sm text-[#b7b7c6] line-clamp-2">{g.description}</p>}
                                                                </div>
                                                                <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest">
                                                                    {isAdmin && (
                                                                        <>
                                                                            <span className="inline-flex items-center gap-1 text-[#facc15]">
                                                                                <Ticket className="w-3.5 h-3.5" /> {g.total_entries} {g.total_entries === 1 ? "entry" : "entries"}
                                                                            </span>
                                                                            <span className="text-[#8b8ba0]">{g.unique_entrants} {g.unique_entrants === 1 ? "player" : "players"}</span>
                                                                        </>
                                                                    )}
                                                                    {!isAdmin && (
                                                                        <span className="inline-flex items-center gap-1 text-[#a855f7]">
                                                                            <Sparkles className="w-3.5 h-3.5" /> More entries = better odds
                                                                        </span>
                                                                    )}
                                                                    {g.my_entries > 0 && (
                                                                        <span className="inline-flex items-center gap-1 text-[#22c55e]">
                                                                            <CheckCircle2 className="w-3.5 h-3.5" /> You: {g.my_entries}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {/* Package selector */}
                                                                <div className="grid grid-cols-3 gap-2">
                                                                    {PACKAGES.map((p) => {
                                                                        const isSel = sel === p.key;
                                                                        return (
                                                                            <button
                                                                                key={p.key}
                                                                                type="button"
                                                                                onClick={() => setSelectedPkg({ ...selectedPkg, [g.id]: p.key })}
                                                                                className={`relative text-left rounded-lg border px-3 py-2.5 transition-all ${
                                                                                    isSel
                                                                                        ? "border-[#facc15] bg-[#1f1a05] shadow-[0_0_0_1px_rgba(250,204,21,0.4)]"
                                                                                        : "border-[#242436] bg-[#0f0f17] hover:border-[#facc15]88"
                                                                                }`}
                                                                                data-testid={`giveaway-pkg-${g.id}-${p.key}`}
                                                                            >
                                                                                {p.badge && (
                                                                                    <span className="absolute -top-2 right-1 text-[9px] font-mono uppercase tracking-widest bg-[#a855f7] text-white px-1.5 py-0.5 rounded">
                                                                                        {p.badge}
                                                                                    </span>
                                                                                )}
                                                                                <p className="font-display text-sm tracking-widest text-white">{p.label}</p>
                                                                                <p className="text-[10px] text-[#8b8ba0] font-mono">${p.price.toFixed(2)}</p>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <Button
                                                                    onClick={() => handleBuy(g, sel)}
                                                                    disabled={busy === buyKey}
                                                                    className="w-full bg-[#facc15] hover:brightness-110 text-[#1a1503] font-bold tracking-widest"
                                                                    data-testid={`giveaway-buy-${g.id}`}
                                                                >
                                                                    {busy === buyKey ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Ticket className="w-4 h-4" /> ENTER — ${pkg.price.toFixed(2)} for {pkg.entries} entr{pkg.entries === 1 ? "y" : "ies"}
                                                                        </span>
                                                                    )}
                                                                </Button>
                                                                <p className="text-[10px] text-[#8b8ba0] text-center font-mono">{pkg.subtitle}</p>
                                                            </CardContent>
                                                        </Card>
                                                    </motion.div>
                                                );
                                            })}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            )}

                            {/* Locked (entries closed, awaiting draw) */}
                            {lockedOnes.length > 0 && (
                                <div data-testid="giveaways-locked-section">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Lock className="w-4 h-4 text-[#facc15]" />
                                        <p className="font-display text-xl tracking-widest text-[#facc15]">AWAITING DRAW ({lockedOnes.length})</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        {lockedOnes.map((g) => (
                                            <Card key={g.id} className="pp-bg-1 border-[#242436] text-white opacity-90" data-testid={`giveaway-locked-${g.id}`}>
                                                <CardContent className="p-5 space-y-3">
                                                    <PrizePreview g={g} />
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className="font-display text-lg tracking-widest text-white truncate">{g.title}</p>
                                                        <Badge className="bg-[#1f1a05] text-[#facc15] border border-[#facc15]55">Locked</Badge>
                                                    </div>
                                                    <p className="text-xs font-mono uppercase text-[#8b8ba0]">
                                                        {isAdmin ? (
                                                            <><Ticket className="inline w-3.5 h-3.5 mr-1" /> {g.total_entries} entries from {g.unique_entrants} players</>
                                                        ) : (
                                                            <>Winner pulled live by the owner</>
                                                        )}
                                                    </p>
                                                    {g.my_entries > 0 && (
                                                        <p className="text-xs font-mono text-[#22c55e]">You have {g.my_entries} entr{g.my_entries === 1 ? "y" : "ies"} — fingers crossed!</p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Past winners */}
                            {drawn.length > 0 && (
                                <div data-testid="giveaways-drawn-section">
                                    <div className="flex items-center gap-2 mb-3">
                                        <PartyPopper className="w-4 h-4 text-[#22c55e]" />
                                        <p className="font-display text-xl tracking-widest text-[#22c55e]">RECENT WINNERS</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        {drawn.map((g) => (
                                            <Card key={g.id} className="pp-bg-1 border-[#242436] text-white" data-testid={`giveaway-drawn-${g.id}`}>
                                                <CardContent className="p-5 space-y-3">
                                                    <PrizePreview g={g} />
                                                    <div>
                                                        <p className="font-display text-lg tracking-widest text-white truncate">{g.title}</p>
                                                        <p className="text-xs font-mono uppercase text-[#22c55e] mt-1">
                                                            <Sparkles className="inline w-3.5 h-3.5 mr-1" /> Winner: @{g.winner_display_name || "—"}
                                                        </p>
                                                        <p className="text-[10px] font-mono text-[#8b8ba0] mt-1">
                                                            {isAdmin ? (
                                                                <>Drawn from {g.total_entries} {g.total_entries === 1 ? "entry" : "entries"}</>
                                                            ) : (
                                                                <>Pulled live by the owner</>
                                                            )}
                                                        </p>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
            <Footer />
            <HelpRequestButton />

            {/* Reserved for future details modal */}
            {buying && (
                <Dialog open={!!buying} onOpenChange={() => setBuying(null)}>
                    <DialogContent className="pp-bg-1 border-[#242436] text-white">
                        <DialogHeader>
                            <DialogTitle>Confirm</DialogTitle>
                            <DialogDescription>You'll be redirected to Stripe to complete your purchase.</DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="secondary" onClick={() => setBuying(null)}>Cancel</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
